import React, { useEffect, useRef, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import FlikLogo from '../../components/Logo/FlikLogo';
import ActivityTypeIcon from '../../activities/ActivityTypeIcon';
import { resolveActivityType, getActivityStats } from '../../activities/activityTypes';
import { getActivity, activityImageUrl } from '../../api/client';
import SenderismoDetail from './SenderismoDetail';
import EscaladaDetail from './EscaladaDetail';
import CiclismoDetail from './CiclismoDetail';
import BarranquismoDetail from './BarranquismoDetail';
import FerrataDetail from './FerrataDetail';
import EspeleologiaDetail from './EspeleologiaDetail';
import './ActivityDetailPage.css';

// Candidate keys for the shared (type-agnostic) fields. Backend field keys are
// admin-defined, so each one is resolved defensively — the first present wins.
export const FIELD_KEYS = {
  description: ['description', 'descripcion', 'desc', 'summary', 'resumen'],
  image: ['image', 'imageUrl', 'photo', 'foto', 'imagen', 'cover', 'portada'],
  location: ['location', 'place', 'lugar', 'ubicacion', 'zona', 'area', 'municipio'],
  region: ['region', 'provincia', 'comarca', 'zone'],
  country: ['country', 'pais', 'país', 'nation', 'nacion', 'estado_pais'],
  altitude: ['altitude', 'altitud', 'elevation', 'cota', 'maxAltitude', 'cotaMaxima'],
  status: ['status', 'estado'],
  gpx: ['gpx', 'gpxUrl', 'track'],
};

/** First non-empty value among `keys` on the values object, or undefined. */
export function pick(values, keys) {
  for (const k of keys) {
    const v = values?.[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return undefined;
}

/** True when a value reads as an affirmative boolean. */
export function isTruthy(v) {
  if (typeof v === 'boolean') return v;
  const s = String(v).trim().toLowerCase();
  return s === 'true' || s === 'sí' || s === 'si' || s === '1' || s === 'yes';
}

// Per-type detail layouts. Types without a bespoke design fall back to the
// generic layout below. Add one entry per type as its design lands.
const DETAIL_BY_TYPE = {
  senderismo: SenderismoDetail,
  escalada: EscaladaDetail,
  ciclismo: CiclismoDetail,
  barranquismo: BarranquismoDetail,
  ferrata: FerrataDetail,
  espeleologia: EspeleologiaDetail,
};

/**
 * Activity detail page (route "/actividad/:id"). Loads the activity, then hands
 * off to the layout for its type (e.g. Senderismo) or a generic fallback. Any
 * field that isn't present in `values` is simply omitted; the weather forecast
 * has no data source yet, so it renders a placeholder.
 */
function ActivityDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Use the activity passed via navigation state for an instant render, then
  // still fetch by id so a direct link / refresh works on its own.
  const [activity, setActivity] = useState(location.state?.activity || null);
  const [loading, setLoading] = useState(!location.state?.activity);

  useEffect(() => {
    let alive = true;
    getActivity(id)
      .then((a) => {
        if (alive && a) setActivity(a);
      })
      .catch(() => {
        // Keep any state-passed activity; the render falls back when we have none.
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [id]);

  if (loading) {
    return <div className="Detail Detail--msg">Cargando actividad…</div>;
  }
  if (!activity) {
    return (
      <div className="Detail Detail--msg">
        <p>No hemos podido cargar esta actividad.</p>
        <button type="button" className="Detail__back" onClick={() => navigate('/')}>
          ← Volver al mapa
        </button>
      </div>
    );
  }

  const type = resolveActivityType(activity.activityType);
  const Layout = DETAIL_BY_TYPE[type.key] || GenericDetail;
  return <Layout activity={activity} />;
}

/* ------------------------------------------------------------------------- *
 *  Shared shell pieces — reused by every per-type layout.
 * ------------------------------------------------------------------------- */

/** Top bar: logo, type, place · altitude, status, and any extra right cell. */
export function DetailTopBar({ activity, rightCell }) {
  const navigate = useNavigate();
  const type = resolveActivityType(activity.activityType);
  const values = activity.values || {};
  const place = pick(values, FIELD_KEYS.location);
  const altitude = pick(values, FIELD_KEYS.altitude);
  const status = pick(values, FIELD_KEYS.status);

  return (
    <header className="Detail__bar">
      <button
        type="button"
        className="Detail__logo"
        onClick={() => navigate('/')}
        aria-label="Volver al mapa"
      >
        <FlikLogo height={18} />
      </button>
      <span className="Detail__barCell Detail__barCell--type">
        <ActivityTypeIcon activityType={activity.activityType} size={14} />
        {type.label.toUpperCase()}
      </span>
      {(place || altitude) && (
        <span className="Detail__barCell">
          {[place && String(place).toUpperCase(), altitude && `${altitude} M`]
            .filter(Boolean)
            .join(' · ')}
        </span>
      )}
      <span className="Detail__barCell Detail__barCell--fill">
        {status ? String(status).toUpperCase() : ''}
      </span>
      {rightCell}
    </header>
  );
}

/** Aspect ratio (w/h) of each variant, used to pick one by container shape. */
const VARIANT_ASPECTS = {
  square: 1,
  twoThirds: 3 / 2,
  half: 2 / 1,
  fullWidth: 10 / 3,
};

/** Variant whose aspect ratio is closest to the container's (w/h). */
function pickVariant(aspect) {
  if (!aspect) return 'fullWidth';
  return Object.keys(VARIANT_ASPECTS).reduce((best, k) =>
    Math.abs(VARIANT_ASPECTS[k] - aspect) < Math.abs(VARIANT_ASPECTS[best] - aspect)
      ? k
      : best
  );
}

/** Hero image (or placeholder) with a back chip and optional extra chips. */
export function DetailHero({ activity, chips, variant }) {
  const navigate = useNavigate();
  const values = activity.values || {};
  // Choose the variant crop by the hero's own shape unless one is forced.
  const heroRef = useRef(null);
  const [heroAspect, setHeroAspect] = useState(null);
  useEffect(() => {
    if (variant) return undefined;
    const el = heroRef.current;
    if (!el) return undefined;
    const update = () => el.clientHeight && setHeroAspect(el.clientWidth / el.clientHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [variant]);
  const activeVariant = variant || pickVariant(heroAspect);

  // Prefer the images linked to the activity (imageIds); fall back to a legacy
  // URL field, then to the placeholder. Each linked image carries the chosen
  // variant crop when one has been set.
  const legacy = pick(values, FIELD_KEYS.image);
  const crops = activity.imageCrops || {};
  const slides = (activity.imageIds || []).length
    ? activity.imageIds.map((id) => ({
        url: activityImageUrl(id),
        crop: crops[id]?.[activeVariant] || null,
      }))
    : legacy
    ? [{ url: legacy, crop: null }]
    : [];
  const [index, setIndex] = useState(0);
  const shown = Math.min(index, Math.max(slides.length - 1, 0));
  const go = (delta) =>
    setIndex((i) => (i + delta + slides.length) % slides.length);

  // Auto-advance every 5s. Depends on `shown` so any manual change (button/dot)
  // restarts the timer instead of firing right after.
  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const t = setInterval(() => setIndex((i) => (i + 1) % slides.length), 15000);
    return () => clearInterval(t);
  }, [slides.length, shown]);
  const locationParts = [
    pick(values, FIELD_KEYS.location),
    pick(values, FIELD_KEYS.region),
    pick(values, FIELD_KEYS.country),
  ]
    .filter(Boolean)
    .map((p) => String(p).toUpperCase());
  return (
    <div className="Detail__hero" ref={heroRef}>
      {locationParts.length > 0 && (
        <span className="Detail__heroLocation">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M12 21 C12 21 5 14 5 9 A7 7 0 0 1 19 9 C19 14 12 21 12 21 Z" />
            <circle cx="12" cy="9" r="2.5" />
          </svg>
          {locationParts.join(' · ')}
        </span>
      )}
      {slides.length ? (
        <>
          {slides.map((slide, i) => {
            const cls = `Detail__heroImg${i === shown ? ' is-active' : ''}`;
            return slide.crop ? (
              <CroppedImage
                key={slide.url}
                className={cls}
                src={slide.url}
                crop={slide.crop}
                alt={activity.name}
              />
            ) : (
              <img key={slide.url} className={cls} src={slide.url} alt={activity.name} />
            );
          })}
          {slides.length > 1 && (
            <>
              <button
                type="button"
                className="Detail__heroNav Detail__heroNav--prev"
                onClick={() => go(-1)}
                aria-label="Imagen anterior"
              >
                ‹
              </button>
              <button
                type="button"
                className="Detail__heroNav Detail__heroNav--next"
                onClick={() => go(1)}
                aria-label="Imagen siguiente"
              >
                ›
              </button>
              <div className="Detail__heroDots">
                {slides.map((slide, i) => (
                  <button
                    key={slide.url}
                    type="button"
                    className={`Detail__heroDot${i === shown ? ' is-active' : ''}`}
                    onClick={() => setIndex(i)}
                    aria-label={`Ir a la imagen ${i + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <div className="Detail__heroPlaceholder" aria-hidden="true">
          <ActivityTypeIcon activityType={activity.activityType} size={40} />
          <span>Sin imagen</span>
        </div>
      )}
      <div className="Detail__heroChips">
        <button type="button" className="Detail__chip Detail__chip--back" onClick={() => navigate('/')}>
          ← Volver al mapa
        </button>
        {chips}
      </div>
    </div>
  );
}

/**
 * Renders a variant crop ({ x, y, width, height } in source pixels) of the full
 * image so it fills its container, cover-style and centred on the crop. Needs
 * the image's natural size (from onload) and the container size (observed), then
 * scales/positions the full <img> so the crop rectangle maps onto the box.
 */
function CroppedImage({ src, crop, alt, className }) {
  const ref = useRef(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [nat, setNat] = useState(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => setBox({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  let imgStyle = { visibility: 'hidden' };
  if (nat && box.w && box.h) {
    const scale = Math.max(box.w / crop.width, box.h / crop.height);
    imgStyle = {
      position: 'absolute',
      width: nat.w * scale,
      height: nat.h * scale,
      maxWidth: 'none',
      left: box.w / 2 - (crop.x + crop.width / 2) * scale,
      top: box.h / 2 - (crop.y + crop.height / 2) * scale,
    };
  }

  return (
    <div ref={ref} className={`Detail__heroCrop ${className || ''}`}>
      <img
        src={src}
        alt={alt}
        draggable={false}
        onLoad={(e) => setNat({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
        style={imgStyle}
      />
    </div>
  );
}

/** Overlapping cream title sheet with eyebrow, title and optional actions. */
export function DetailTitleSheet({ eyebrow, title, actions }) {
  return (
    <div className="Detail__titleSheet flik-cut-xl">
      <div className="Detail__titleText">
        {eyebrow && <span className="Detail__eyebrow">{eyebrow}</span>}
        <h1 className="Detail__title">{title}</h1>
      </div>
      {actions && <div className="Detail__titleActions">{actions}</div>}
    </div>
  );
}

// Line-style weather icons (mono, currentColor) to match FLIK's identity —
// no emoji. `w` selects the glyph from the Open-Meteo WMO weather code.
function WeatherGlyph({ code }) {
  const p = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' };
  const sun = <circle cx="12" cy="12" r="4.2" {...p} />;
  const rays = [0, 45, 90, 135, 180, 225, 270, 315].map((a) => {
    const r = (a * Math.PI) / 180;
    return <line key={a} x1={12 + 6 * Math.cos(r)} y1={12 + 6 * Math.sin(r)} x2={12 + 8 * Math.cos(r)} y2={12 + 8 * Math.sin(r)} {...p} />;
  });
  const cloud = <path d="M7 17h9.5a3.2 3.2 0 0 0 .3-6.4A5 5 0 0 0 7 12a2.5 2.5 0 0 0 0 5z" {...p} />;
  const drops = (n) => Array.from({ length: n }).map((_, i) => <line key={i} x1={8 + i * 4} y1="19" x2={7 + i * 4} y2="22" {...p} />);

  let glyph;
  if (code === 0) glyph = <g>{sun}{rays}</g>;
  else if (code <= 3) glyph = <g><circle cx="9" cy="9" r="3.2" {...p} />{cloud}</g>;
  else if (code <= 48) glyph = <g><line x1="5" y1="10" x2="19" y2="10" {...p} /><line x1="5" y1="13.5" x2="19" y2="13.5" {...p} /><line x1="7" y1="17" x2="17" y2="17" {...p} /></g>;
  else if (code <= 67) glyph = <g>{cloud}{drops(3)}</g>; // llovizna / lluvia
  else if (code <= 77) glyph = <g>{cloud}<g {...p}><line x1="9" y1="20" x2="9" y2="20" /><line x1="13" y1="20" x2="13" y2="20" /><line x1="11" y1="22" x2="11" y2="22" /></g></g>; // nieve
  else if (code <= 82) glyph = <g>{cloud}{drops(2)}</g>; // chubascos
  else glyph = <g>{cloud}<path d="M12 18l-2 4h3l-2 4" {...p} /></g>; // tormenta

  return <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">{glyph}</svg>;
}

const WindIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
    <path d="M3 8h9a2.5 2.5 0 1 0-2.5-2.5M3 12h13a2.5 2.5 0 1 1-2.5 2.5M3 16h7" />
  </svg>
);
const RainIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 14h9.5a3.2 3.2 0 0 0 .3-6.4A5 5 0 0 0 7 9a2.5 2.5 0 0 0 0 5z" />
    <line x1="9" y1="17" x2="8" y2="20" /><line x1="13" y1="17" x2="12" y2="20" />
  </svg>
);

const WEEKDAYS = ['DOM', 'LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB'];
function dayLabel(dateStr) {
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return `${WEEKDAYS[d.getDay()]} ${d.getDate()}`;
}
const round = (n) => (n == null ? '—' : Math.round(n));

// Local YYYY-MM-DD for today + `offset` days (avoids UTC drift of toISOString).
function dateKey(offset) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Summary cell for the 3-day strip (ayer / hoy / mañana). */
function WeatherStripCell({ d, label }) {
  return (
    <div className="Detail__weatherCell Detail__weatherCell--data" title={d.condition}>
      <span className="Detail__weatherDay">{label}</span>
      <span className="Detail__weatherIcon"><WeatherGlyph code={d.weatherCode} /></span>
      <span className="Detail__weatherTemp">{round(d.temperatureMax)}°/{round(d.temperatureMin)}°</span>
      <span className="Detail__weatherFlags">
        {d.windSpeedMax >= 25 && <span title={`Viento ${round(d.windSpeedMax)} km/h`}><WindIcon /></span>}
        {d.precipitationProbability >= 50 && <span title={`Precipitación ${round(d.precipitationProbability)}%`}><RainIcon /></span>}
      </span>
    </div>
  );
}

/** Full 7-day forecast modal — one column per day, richer per-day data. */
function WeatherModal({ days, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="Detail__weatherOverlay" onClick={onClose} role="presentation">
      <div className="Detail__weatherModal flik-cut-lg" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Previsión a 7 días">
        <div className="Detail__weatherModalHead">
          <span className="Detail__sectionLabel Detail__sectionLabel--gold">PREVISIÓN · 7 DÍAS</span>
          <button type="button" className="Detail__weatherClose" onClick={onClose} aria-label="Cerrar">✕</button>
        </div>
        <div className="Detail__weather7" style={{ gridTemplateColumns: `repeat(${days.length}, minmax(88px, 1fr))` }}>
          {days.map((d) => (
            <div key={d.date} className="Detail__weatherCell Detail__weatherCell--data">
              <span className="Detail__weatherDay">{dayLabel(d.date)}</span>
              <span className="Detail__weatherIcon"><WeatherGlyph code={d.weatherCode} /></span>
              <span className="Detail__weatherTemp">{round(d.temperatureMax)}°/{round(d.temperatureMin)}°</span>
              <span className="Detail__weatherCond">{d.condition}</span>
              <dl className="Detail__weatherMeta">
                <div><dt>Lluvia</dt><dd>{round(d.precipitationProbability)}%</dd></div>
                <div><dt>Viento</dt><dd>{round(d.windSpeedMax)} km/h</dd></div>
                <div><dt>Nubes</dt><dd>{round(d.cloudCover)}%</dd></div>
              </dl>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Weather forecast from the activity's daily array; placeholder when absent. */
export function WeatherPlaceholder({ activity }) {
  const days = activity?.weather || activity?.forecast || activity?.weatherForecast || [];
  const [open, setOpen] = useState(false);

  if (!days.length) {
    return (
      <section className="Detail__section">
        <div className="Detail__sectionLabel Detail__sectionLabel--gold">PARTE METEOROLÓGICO</div>
        <div className="Detail__weather">
          {['−2 DÍAS', 'HOY', '+4 DÍAS'].map((d) => (
            <div key={d} className="Detail__weatherCell">
              <span className="Detail__weatherDay">{d}</span>
              <span className="Detail__weatherDash">—°/—°</span>
            </div>
          ))}
        </div>
        <p className="Detail__weatherNote">Previsión no disponible todavía.</p>
      </section>
    );
  }

  // Strip = ayer / hoy / mañana by real date; fall back to the first three.
  const byDate = new Map(days.map((d) => [d.date, d]));
  const strip = [
    { label: 'AYER', d: byDate.get(dateKey(-1)) },
    { label: 'HOY', d: byDate.get(dateKey(0)) },
    { label: 'MAÑANA', d: byDate.get(dateKey(1)) },
  ].filter((x) => x.d);
  const cells = strip.length
    ? strip
    : days.slice(0, 3).map((d) => ({ label: dayLabel(d.date), d }));

  return (
    <section className="Detail__section">
      <div className="Detail__sectionLabel Detail__sectionLabel--gold">PARTE METEOROLÓGICO</div>
      <div className="Detail__weather" style={{ gridTemplateColumns: `repeat(${cells.length}, 1fr)` }}>
        {cells.map(({ d, label }) => (
          <WeatherStripCell key={d.date} d={d} label={label} />
        ))}
      </div>
      <button type="button" className="Detail__weather7Btn" onClick={() => setOpen(true)}>
        VER 7 DÍAS →
      </button>
      {open && <WeatherModal days={days} onClose={() => setOpen(false)} />}
    </section>
  );
}

/* ------------------------------------------------------------------------- *
 *  Generic fallback layout for types without a bespoke design yet.
 * ------------------------------------------------------------------------- */

function GenericDetail({ activity }) {
  const type = resolveActivityType(activity.activityType);
  const values = activity.values || {};
  const description = pick(values, FIELD_KEYS.description);
  const place = pick(values, FIELD_KEYS.location);
  const gpx = pick(values, FIELD_KEYS.gpx);

  const allStats = getActivityStats(activity);
  const mideStat = allStats.find((s) => s.kind === 'mide' && s.present);
  const gridStats = allStats.filter((s) => s !== mideStat && s.present);
  const mideParts = mideStat ? String(mideStat.value).split('-') : [];

  return (
    <div className="Detail">
      <DetailTopBar activity={activity} />
      <DetailHero activity={activity} />
      <DetailTitleSheet
        eyebrow={[place && String(place).toUpperCase(), type.label.toUpperCase()]
          .filter(Boolean)
          .join(' · ')}
        title={activity.name}
      />
      <div className="Detail__body">
        <div className="Detail__col">
          {description && (
            <section className="Detail__section">
              <div className="Detail__sectionLabel">DESCRIPCIÓN</div>
              <p className="Detail__desc">{description}</p>
            </section>
          )}
          {mideStat && <MideBlock parts={mideParts} />}
        </div>
        <div className="Detail__col">
          {gridStats.length > 0 && (
            <div className="Detail__stats">
              {gridStats.map((stat) => (
                <div key={stat.unit} className="Detail__stat">
                  <span className="Detail__statLabel">{stat.unit.toUpperCase()}</span>
                  <span className="Detail__statVal">
                    {stat.kind === 'bool' ? (stat.value ? 'Sí' : 'No') : stat.value}
                  </span>
                </div>
              ))}
            </div>
          )}
          <WeatherPlaceholder activity={activity} />
          {gpx && (
            <a className="Detail__gpx flik-cut-md" href={gpx} download>
              <span>SIGUIENTE PASO</span>
              <strong>DESCARGAR GPX</strong>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

const MIDE_LABELS = ['MEDIO', 'ITINERARIO', 'DESPLAZ.', 'ESFUERZO'];

/** MIDE breakdown block (four levels), shared by layouts that show it. */
export function MideBlock({ parts }) {
  return (
    <div className="Detail__mide flik-cut-lg">
      <div className="Detail__mideHead">
        <span>DIFICULTAD · MIDE</span>
        <span>{parts.join(' · ')}</span>
      </div>
      <div className="Detail__mideGrid">
        {MIDE_LABELS.map((label, i) => (
          <div key={label} className="Detail__mideCell">
            <span className="Detail__mideVal">{parts[i] ?? '?'}</span>
            <span className="Detail__mideLabel">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ActivityDetailPage;
